// ===========================================================================
// BillingDashboard — Billing & Revenue Integrity (preliminar).
//
// Datos REALES: cost of care + coding/recapture (ceoDataService).
// VALORES ESTÁNDAR (marcados "est."): RAF$ por oportunidad y el bloque de
// revenue cycle (claims/AR/denials/payer mix) hasta que el backend lo exponga.
// Ver docs/modulos/dashboards/BILLING_DASHBOARD.md.
// ===========================================================================
import React from "react";
import {
  Box,
  Stack,
  Typography,
  Alert,
  Chip,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaidIcon from "@mui/icons-material/Paid";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import GroupsIcon from "@mui/icons-material/Groups";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import { ResponsiveLine } from "@nivo/line";

import { DashboardHeader, MetricCard, ChartCard } from "../shared";
import useBillingDashboard from "../../hooks/useBillingDashboard";
import {
  formatMoney,
  formatMoneyCompact,
  formatNumber,
  STANDARD_REVENUE_CYCLE,
  STANDARD_AR_AGING,
  STANDARD_PAYER_MIX,
} from "../../constants/billingDashboard";

const PIE_COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#06B6D4"];

const EstChip = () => (
  <Tooltip title="Standard estimate — not yet from the billing backend">
    <Chip label="est." size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: "0.6rem", fontWeight: 700 }} />
  </Tooltip>
);

function SectionTitle({ icon, children, standard }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5, mt: 1 }}>
      {icon}
      <Typography variant="h6" fontWeight={700}>
        {children}
      </Typography>
      {standard && (
        <Chip
          icon={<InfoOutlinedIcon />}
          label="Requires billing backend"
          size="small"
          color="warning"
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      )}
    </Stack>
  );
}

export default function BillingDashboard() {
  const { loading, error, cost, kpis, captureByType, topHccByValue } = useBillingDashboard();

  // Cost breakdown (sin la fila total "Cost of Care").
  const costBreakdown = (cost?.summary?.breakdown || []).filter(
    (b) => b.costType !== "Cost of Care"
  );
  const pieCost = costBreakdown.map((b) => ({
    id: b.costType.replace(" Cost", ""),
    label: b.costType.replace(" Cost", ""),
    value: Math.round(b.cost),
  }));

  // Tendencia mensual (total vs inpatient).
  const months = cost?.monthly?.months || [];
  const lineData = [
    {
      id: "Total cost",
      data: months.map((m) => ({ x: m.month, y: Math.round(m.total) })),
    },
    {
      id: "Inpatient",
      data: months.map((m) => ({ x: m.month, y: Math.round(m.inpatient) })),
    },
  ];

  const barCapture = captureByType.map((c) => ({
    type: c.type,
    "Estimated $": c.estValue,
    count: c.count,
  }));

  return (
    <Stack spacing={2.5}>
      <DashboardHeader
        title="Billing & Revenue Integrity"
        subtitle="Risk-adjustment revenue capture, cost of care and revenue-cycle health for the billing team"
        icon={<ReceiptLongIcon />}
        color="success"
      />

      <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ borderRadius: 3 }}>
        <b>Preliminary dashboard.</b> Revenue capture & cost figures are real (coding + cost of care).
        Items marked <EstChip /> and the <b>Revenue cycle</b> section use standard values until the
        billing/claims backend (AR, denials, payments, payer mix) is available.
      </Alert>

      {error && (
        <Alert severity="warning" sx={{ borderRadius: 3 }}>
          Some data could not be loaded. Showing what is available.
        </Alert>
      )}

      {/* ── KPI row ─────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)", lg: "repeat(6, 1fr)" },
        }}
      >
        <MetricCard
          title="Revenue at risk"
          value={loading ? "…" : formatMoneyCompact(kpis.revenueAtRisk)}
          subtitle="Open HCC opportunities · est."
          icon={<PaidIcon />}
          color="warning"
          loading={loading}
        />
        <MetricCard
          title="Open coding opps"
          value={loading ? "…" : formatNumber(kpis.openOpps)}
          subtitle={`${formatNumber(kpis.uniquePatients)} patients`}
          icon={<AssignmentTurnedInIcon />}
          color="error"
          loading={loading}
        />
        <MetricCard
          title="Recapture rate"
          value={loading ? "…" : `${kpis.recaptureRate}%`}
          subtitle="Addressed / total"
          icon={<TrendingDownIcon />}
          color="info"
          loading={loading}
        />
        <MetricCard
          title="Cost of care"
          value={loading ? "…" : formatMoneyCompact(kpis.totalCostOfCare)}
          subtitle="Total, all centers"
          icon={<LocalAtmIcon />}
          color="secondary"
          loading={loading}
        />
        <MetricCard
          title="Claims processed"
          value={loading ? "…" : formatNumber(kpis.claimsProcessed)}
          subtitle="From facility claims"
          icon={<ReceiptLongIcon />}
          color="primary"
          loading={loading}
        />
        <MetricCard
          title="Avg cost / patient"
          value={loading ? "…" : formatMoneyCompact(kpis.avgCostPerPatient)}
          subtitle={`${formatNumber(kpis.highCostPatients)} high-cost`}
          icon={<GroupsIcon />}
          color="success"
          loading={loading}
        />
      </Box>

      {/* ── Revenue capture + cost breakdown ────────────────────────────── */}
      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
        <ChartCard
          title="Revenue capture pipeline"
          subtitle="Open HCC opportunities by condition type · estimated value"
          color="success"
          loading={loading}
          height={280}
        >
          {barCapture.length > 0 && (
            <ResponsiveBar
              data={barCapture}
              keys={["Estimated $"]}
              indexBy="type"
              margin={{ top: 10, right: 20, bottom: 40, left: 70 }}
              padding={0.35}
              colors={["#10B981"]}
              borderRadius={4}
              axisLeft={{ format: (v) => formatMoneyCompact(v) }}
              valueFormat={(v) => formatMoneyCompact(v)}
              labelSkipWidth={48}
              tooltip={({ data }) => (
                <Box sx={{ bgcolor: "#111", color: "#fff", px: 1.5, py: 1, borderRadius: 1, fontSize: 12 }}>
                  <b>{data.type}</b>: {formatMoney(data["Estimated $"])} · {formatNumber(data.count)} opps
                </Box>
              )}
              theme={{ fontSize: 11 }}
            />
          )}
        </ChartCard>

        <ChartCard
          title="Cost of care breakdown"
          subtitle="Where the spend goes (real)"
          color="secondary"
          loading={loading}
          height={280}
        >
          {pieCost.length > 0 && (
            <ResponsivePie
              data={pieCost}
              margin={{ top: 16, right: 16, bottom: 16, left: 16 }}
              innerRadius={0.55}
              padAngle={1}
              cornerRadius={3}
              colors={PIE_COLORS}
              valueFormat={(v) => formatMoneyCompact(v)}
              arcLabelsSkipAngle={18}
              enableArcLinkLabels={false}
              theme={{ fontSize: 11 }}
            />
          )}
        </ChartCard>
      </Box>

      {/* ── Monthly trend ───────────────────────────────────────────────── */}
      <ChartCard
        title="Monthly cost trend"
        subtitle="Total vs inpatient cost (real)"
        color="primary"
        loading={loading}
        height={260}
      >
        {months.length > 0 && (
          <ResponsiveLine
            data={lineData}
            margin={{ top: 16, right: 24, bottom: 50, left: 70 }}
            xScale={{ type: "point" }}
            yScale={{ type: "linear", min: 0, max: "auto" }}
            curve="monotoneX"
            colors={["#3B82F6", "#EF4444"]}
            axisBottom={{ tickRotation: -35 }}
            axisLeft={{ format: (v) => formatMoneyCompact(v) }}
            enablePoints
            pointSize={7}
            useMesh
            enableArea
            areaOpacity={0.08}
            legends={[
              {
                anchor: "top-right",
                direction: "row",
                translateY: -8,
                itemWidth: 90,
                itemHeight: 18,
                symbolSize: 10,
              },
            ]}
            theme={{ fontSize: 11 }}
          />
        )}
      </ChartCard>

      {/* ── Top HCC opportunities by value ──────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 4 }}>
        <SectionTitle icon={<PaidIcon color="success" />}>
          Top HCC opportunities by estimated value
        </SectionTitle>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>HCC</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align="right">Open</TableCell>
              <TableCell align="right">
                Est. value <EstChip />
              </TableCell>
              <TableCell>Priority</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {topHccByValue.map((h) => (
              <TableRow key={h.hccId} hover>
                <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>{h.hccId}</TableCell>
                <TableCell>{h.description}</TableCell>
                <TableCell align="right">{formatNumber(h.count)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: "success.main" }}>
                  {formatMoney(h.estValue)}
                </TableCell>
                <TableCell>
                  <Chip
                    label={h.priority || "—"}
                    size="small"
                    color={h.priority === "HIGH" ? "error" : h.priority === "MEDIUM" ? "warning" : "default"}
                    variant="outlined"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* ── Revenue cycle (STANDARD / requires billing backend) ─────────── */}
      <Box>
        <SectionTitle icon={<ReceiptLongIcon color="warning" />} standard>
          Revenue cycle
        </SectionTitle>

        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
            mb: 2,
          }}
        >
          <MetricCard title="Clean claim rate" value={`${STANDARD_REVENUE_CYCLE.cleanClaimRatePct}%`} subtitle="est." color="success" />
          <MetricCard title="Denial rate" value={`${STANDARD_REVENUE_CYCLE.denialRatePct}%`} subtitle="est." color="error" />
          <MetricCard title="Days in A/R" value={STANDARD_REVENUE_CYCLE.daysInAR} subtitle="est." color="warning" />
          <MetricCard title="First-pass yield" value={`${STANDARD_REVENUE_CYCLE.firstPassYieldPct}%`} subtitle="est." color="info" />
        </Box>

        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
          <ChartCard title="A/R aging" subtitle="Standard placeholder" color="primary" height={240}>
            <ResponsiveBar
              data={STANDARD_AR_AGING}
              keys={["amount"]}
              indexBy="bucket"
              margin={{ top: 10, right: 16, bottom: 40, left: 70 }}
              padding={0.35}
              colors={({ data }) =>
                data.bucket === "90+" ? "#EF4444" : data.bucket === "61–90" ? "#F59E0B" : "#3B82F6"
              }
              borderRadius={4}
              axisLeft={{ format: (v) => formatMoneyCompact(v) }}
              valueFormat={(v) => formatMoneyCompact(v)}
              theme={{ fontSize: 11 }}
            />
          </ChartCard>

          <ChartCard title="Payer mix" subtitle="Standard placeholder" color="secondary" height={240}>
            <ResponsivePie
              data={STANDARD_PAYER_MIX}
              margin={{ top: 16, right: 16, bottom: 16, left: 16 }}
              innerRadius={0.55}
              padAngle={1}
              cornerRadius={3}
              colors={PIE_COLORS}
              valueFormat={(v) => `${v}%`}
              arcLabelsSkipAngle={18}
              enableArcLinkLabels={false}
              theme={{ fontSize: 11 }}
            />
          </ChartCard>
        </Box>
      </Box>

      <Box sx={{ pb: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Revenue-cycle metrics (clean claim rate, denials, A/R, payer mix) are standard placeholders.
          See docs/modulos/dashboards/BILLING_DASHBOARD.md for the backend endpoints required to make them real.
        </Typography>
      </Box>
    </Stack>
  );
}
